import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { glossaryTerms } from '@/content/glossary';
import { useSeo } from '@/seo/useSeo';

export function Glossary(): JSX.Element {
  useSeo({
    title: 'MSP glossary',
    description:
      'Plain-language definitions for the RMM/PSA industry’s most-used terms — MSP, RMM, PSA, agentic AI, vCIO, QBR, multi-tenancy, patch management, SLA, and credential vault.',
    path: '/glossary',
    schema: {
      '@type': 'ItemList',
      itemListElement: glossaryTerms.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://nexusrmm.ai/glossary/${t.slug}`,
        name: t.term,
      })),
    },
  });
  return (
    <div>
      <PageHero
        eyebrow="Glossary"
        title="The MSP/RMM/PSA terms, defined plainly."
        subtitle="Industry vocabulary, not a product pitch — what each term actually means, why it matters, and how Nexus specifically handles it, if you want to see the tie-in."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {glossaryTerms.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.slug} delay={(i % 3) * 80}>
                  <Link
                    to={`/glossary/${t.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h2 className="font-display text-[17px] font-semibold text-nexus-ink">{t.term}</h2>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{t.shortDefinition}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                See these terms as working software.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort for a real walkthrough, not a definition.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
